import { Input } from '@innowise-group/mui-kit';
import { PhoneInput } from '@shared-mui-components';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Styled from './contact-section.styles';
import { useControlFormField, useValidationSchemaCandidate } from '@innowise-group/core';
import { MaxRefsCount } from '@constants';
import { Dropzone } from '../dropzone';

interface ContactSectionProps {
  disabled?: boolean;
  isDiff?: boolean;
}

const ContactSection: React.FC<ContactSectionProps> = ({ disabled, isDiff }) => {
  const { t } = useTranslation();

  const { control, watch } = useFormContext();
  const links = watch('candidateLinks');

  const { validationSchema } = useValidationSchemaCandidate();
  const { handleAddItem, handleRemoveItem, handleChangeInputItem, handlerValidate } =
    useControlFormField(validationSchema);

  const handleRemoveLink = (index: number) => () => {
    handleRemoveItem<{ name: string; url: string }>(index, 'candidateLinks', links)();
    handlerValidate('candidateLinks');
  };

  return (
    <React.Fragment>
      <Styled.BoxForDiffConsolidation>
        {isDiff && <Styled.CustomPrimitiveFieldsDiff fieldName="email" />}
        <Controller
          name="email"
          control={control}
          render={({ field: { onChange, value, ref }, formState: { errors } }) => (
            <Input
              label="E-mail"
              disabled={disabled}
              error={!!errors['email']?.message}
              helperText={errors['email']?.message.toString()}
              value={value}
              onChange={onChange}
              inputRef={ref}
              placeholder={t('pages.candidates.candidateCreation.enterTheEmail')}
            />
          )}
        />
      </Styled.BoxForDiffConsolidation>
      <Styled.BoxForDiffConsolidation>
        {isDiff && <Styled.CustomContactsFieldsDiff fieldName="linkedIn" />}
        <Controller
          name="candidateContacts.1.contact.value"
          control={control}
          render={({ field: { onChange, value, ref }, formState: { errors } }) => (
            <Input
              label="LinkedIn"
              disabled={disabled}
              error={!!errors?.candidateContacts?.[1]?.contact?.value?.message}
              helperText={errors?.candidateContacts?.[1]?.contact?.value?.message}
              value={value}
              onChange={onChange}
              inputRef={ref}
              placeholder={t('pages.candidates.candidateCreation.enterTheURL')}
            />
          )}
        />
      </Styled.BoxForDiffConsolidation>
      <Styled.BoxForDiffConsolidation>
        {isDiff && <Styled.CustomContactsFieldsDiff fieldName="github" />}
        <Controller
          name="candidateContacts.2.contact.value"
          control={control}
          render={({ field: { onChange, value, ref }, formState: { errors } }) => (
            <Input
              label="GitHub"
              disabled={disabled}
              error={!!errors?.candidateContacts?.[2]?.contact?.value?.message}
              helperText={errors?.candidateContacts?.[2]?.contact?.value?.message}
              value={value}
              inputRef={ref}
              onChange={onChange}
              placeholder={t('pages.candidates.candidateCreation.enterTheURL')}
            />
          )}
        />
      </Styled.BoxForDiffConsolidation>
      <Styled.BoxForDiffConsolidation>
        {isDiff && <Styled.CustomContactsFieldsDiff fieldName="behance" />}
        <Controller
          name="candidateContacts.5.contact.value"
          control={control}
          render={({ field: { onChange, value, ref }, formState: { errors } }) => (
            <Input
              label="Behance"
              disabled={disabled}
              inputRef={ref}
              error={!!errors?.candidateContacts?.[5]?.contact?.value?.message}
              helperText={errors?.candidateContacts?.[5]?.contact?.value?.message}
              value={value}
              onChange={onChange}
              placeholder={t('pages.candidates.candidateCreation.enterTheURL')}
            />
          )}
        />
      </Styled.BoxForDiffConsolidation>
      <Styled.BoxForDiffConsolidation>
        {isDiff && <Styled.CustomContactsFieldsDiff fieldName="phone" />}
        <Controller
          name="candidateContacts.3.contact"
          control={control}
          render={({ field: { onChange, value, ref }, formState: { errors } }) => (
            <PhoneInput
              name="candidateContacts.3.contact"
              value={value}
              onChange={onChange}
              fullWidth
              inputRef={ref}
              disabled={disabled}
              label={t('pages.candidates.candidateCreation.phoneNumber')}
              error={!!errors?.candidateContacts?.[3]?.contact?.value?.message}
              helperText={errors?.candidateContacts?.[3]?.contact?.value?.message}
              placeholder={t('pages.candidates.candidateCreation.enterTheNumber')}
            />
          )}
        />
      </Styled.BoxForDiffConsolidation>
      <Styled.BoxForDiffConsolidation>
        {isDiff && <Styled.CustomContactsFieldsDiff fieldName="additionalPhone" />}
        <Controller
          name="candidateContacts.4.contact"
          control={control}
          render={({ field: { onChange, value, ref }, formState: { errors } }) => (
            <PhoneInput
              name="candidateContacts.4.contact"
              value={value}
              onChange={onChange}
              fullWidth
              inputRef={ref}
              disabled={disabled}
              label={t('pages.candidates.candidateCreation.optionalPhoneNumber')}
              error={!!errors?.candidateContacts?.[4]?.contact?.value?.message}
              helperText={errors?.candidateContacts?.[4]?.contact?.value?.message}
              placeholder={t('pages.candidates.candidateCreation.enterTheNumber')}
            />
          )}
        />
      </Styled.BoxForDiffConsolidation>
      <Styled.BoxForDiffConsolidation>
        {isDiff && <Styled.CustomContactsFieldsDiff fieldName="telegram" />}
        <Controller
          name="candidateContacts.0.contact.value"
          control={control}
          render={({ field: { onChange, value, ref }, formState: { errors } }) => (
            <Input
              label="Telegram"
              disabled={disabled}
              error={!!errors?.candidateContacts?.[0]?.contact?.value?.message}
              helperText={errors?.candidateContacts?.[0]?.contact?.value?.message}
              value={value}
              onChange={onChange}
              inputRef={ref}
              placeholder={t('pages.candidates.candidateCreation.enterTheNickname')}
            />
          )}
        />
      </Styled.BoxForDiffConsolidation>
      <Styled.BoxForDiffConsolidation>
        {isDiff && <Styled.CustomContactsFieldsDiff fieldName="skype" />}
        <Controller
          name="candidateContacts.6.contact.value"
          control={control}
          render={({ field: { onChange, value, ref }, formState: { errors } }) => (
            <Input
              label="Skype"
              disabled={disabled}
              error={!!errors?.candidateContacts?.[6]?.contact?.value?.message}
              helperText={errors?.candidateContacts?.[6]?.contact?.value?.message}
              value={value}
              inputRef={ref}
              onChange={onChange}
              placeholder={t('pages.candidates.candidateCreation.enterTheNickname')}
            />
          )}
        />
      </Styled.BoxForDiffConsolidation>
      <Styled.BoxForLinksDiffConsolidation $fullWidth={links?.length > 1}>
        {isDiff && <Styled.CustomLinksDataDiff />}
        {links?.map((_, index) => (
          <React.Fragment key={index}>
            <Styled.DeleteLinkContainer>
              <Controller
                name={`candidateLinks.${index}.name`}
                control={control}
                render={({ field: { onChange, value, ref }, formState: { errors } }) => (
                  <Input
                    disabled={disabled}
                    value={value}
                    inputRef={ref}
                    label={t('pages.candidates.candidateCreation.linkName')}
                    onChange={handleChangeInputItem(onChange, 'candidateLinks')}
                    error={!!(errors?.candidateLinks?.[index]?.name?.message || errors?.candidateLinks?.message)}
                    helperText={errors?.candidateLinks?.[index]?.name?.message || errors?.candidateLinks?.message}
                    placeholder={t('pages.candidates.candidateCreation.enterTheName')}
                  />
                )}
              />
            </Styled.DeleteLinkContainer>
            <Styled.DeleteLinkContainer>
              {links.length > 1 && (
                <Styled.DeleteButton
                  startIcon={<Styled.DeleteIcon type="u_plus" />}
                  disabled={disabled}
                  onClick={handleRemoveLink(index)}
                  variant="text"
                >
                  {t('buttons.delete')}
                </Styled.DeleteButton>
              )}
              <Controller
                name={`candidateLinks.${index}.url`}
                control={control}
                render={({ field: { onChange, value, ref }, formState: { errors } }) => (
                  <Input
                    disabled={disabled}
                    label={t('pages.candidates.candidateCreation.link')}
                    value={value}
                    inputRef={ref}
                    onChange={handleChangeInputItem(onChange, 'candidateLinks')}
                    error={
                      !!errors?.candidateLinks?.[index]?.url?.message ||
                      errors?.candidateLinks?.[index]?.message ||
                      errors?.candidateLinks?.message
                    }
                    helperText={
                      errors?.candidateLinks?.[index]?.url?.message ||
                      errors?.candidateLinks?.[index]?.message ||
                      errors?.candidateLinks?.message
                    }
                    placeholder={t('pages.candidates.candidateCreation.addTheLink')}
                  />
                )}
              />
            </Styled.DeleteLinkContainer>
          </React.Fragment>
        ))}
      </Styled.BoxForLinksDiffConsolidation>
      {(links?.length < MaxRefsCount || !links) && (
        <Styled.AddButton
          disabled={disabled}
          onClick={handleAddItem('candidateLinks', links, { name: '', url: '' })}
          variant="text"
          startIcon={<Styled.PlusIcon type="u_plus" />}
        >
          {t('pages.candidates.candidateCreation.addLink')}
        </Styled.AddButton>
      )}
      <Styled.Divider />

      <Dropzone fieldName="filesMeta" disabled={disabled} />
    </React.Fragment>
  );
};

export default React.memo(ContactSection);
